import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getMasterShipperOptions } from '@/services/master/userManagement/apis'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { shipperScopeOf } from '@/components/system/shipperScope'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { usePboQueryErrorToast } from '@/hooks/usePboQueryErrorToast'
import { failureError, invalidError, notice } from '@/components/pbo/notice'
import { authManagementQueryKeys, useGetAuthManagementList } from './queries/useGetAuthManagementList'
import type { AuthManagementParams, AuthManagementRow } from './queries/useGetAuthManagementList'
import { useDeleteAuthorities, useGetAuthorityMenus, useSaveAuthorityHead, useSaveAuthorityMenus } from './queries/useAuthorityMenus'
import { cascadeMenuAct, fromSearchQuery } from './queries/mapping'
import { BUTTON_LEVEL_OPTIONS, ITEM_EDIT_CASCADE, MESSAGES, YN_OPTIONS } from './consts'
import type { AuthMenuRow } from './consts'
import { filterHelperRows } from '@/utils/filterHelperRows'
import AuthManagementPageView, { type CellOption, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'

/**
 * 권한관리(MA03) — 1.0 MA03 을 pbo-core 로 재구현한 화면(dev 라우트 ma16).
 *
 * 겉(조회영역·상하 분할 표·확인창·권한코드 검색 모달)은 컨덕터 뷰(`./handoff/page.generated`)가 그리고, 이 파일은 옛 훅·API·
 * 세션/URL 유지를 그대로 두고 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드, 컨덕터 문서 51). 옛 조회영역·표 컴포넌트(`components/`)와
 * 플래그 분기(useFeatureFlag + v2/page)는 없앴다 — 화면은 하나다(2026-09-11 규칙). 데이터 훅(`queries/`)은 그대로다.
 *
 * 마스터-디테일: 상단 권한 표에서 **행을 클릭**(onPick)한 권한의 메뉴를 하단에 조회한다(체크 선택이 아니다). 체크 선택은 저장·삭제 대상.
 * 화주 잠금: 통합 운영 화주(FREE_SHIPPERS 4종)만 화주를 고른다. 화주 고정 계정은 옵션·복원값·조회·기본값·저장 행의 화주코드가 현재 화주로 묶인다.
 *
 * 상단 [추가](hdAddButton 슬롯, 컨덕터 #592) — 빈 권한 행을 맨 위에 넣고(화주 = 조회 화주), 상단 [저장] 때 신규 행은 POST 로 가른다(옛 appendHeadRow·isNew).
 * 데모에 있고 dev 에 없는 것(개발팀 몫, 여기서는 막고 안내한다):
 *  · 하단 [추가] — 메뉴 매핑 추가 API 가 없다(권한 생성 시 전체 메뉴가 자동 매핑). TODO(api)
 *  · 하단 [삭제] — 메뉴 매핑 삭제 API 가 없다(비활성 = 허용여부 N). TODO(api)
 */
/** __new = [추가]로 생긴 미저장 행 — DS 표 계약(처음부터 필수 미입력을 빨갛게 표기, 데모와 같다). 저장 때 POST, 삭제 때는 로컬 제거만. */
type HeadRow = AuthManagementRow & Row & { key: string; __new?: boolean }
type ItemRow = AuthMenuRow & Row & { key: string }
type ShipperOption = { value: string; label: string }
type Master = { shipperCode: string; authCode: string }

/** 권한코드 검색 모달의 조회 칸(생성 뷰 #ma03AuthSearchModal) — 화주명은 고정 표시 칸이라 조건이 아니다. */
const AUTH_HELPER_KEYS = ['AUTH_CD', 'AUTH_NM'] as const
const HEAD_SCOPE = 'hd-tableGroup'
const ITEM_SCOPE = 'it-tableGroup'

/** 조회영역 값(키 = filterKey) → BE 조회 조건. 데모는 권한코드가 단건 입력(옛 화면은 다건 — 데모가 정본). */
const toParams = (filters: Record<string, unknown>): AuthManagementParams => {
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
  const auth = text(filters.AUTH_CD)
  return { shipperCode: text(filters.SPR_NM), authCodes: auth ? [auth] : undefined }
}

/** 되살린 조회 조건 → 조회영역 값(키 = filterKey). 없는 값은 얹지 않는다. 다건은 첫 값만(입력이 단건). */
const toFilterValues = (params: AuthManagementParams | undefined): Record<string, unknown> => {
  if (!params) return {}
  return { ...(params.shipperCode ? { SPR_NM: params.shipperCode } : {}), ...(params.authCodes?.[0] ? { AUTH_CD: params.authCodes[0] } : {}) }
}

/** BE meta.message 우선, 빈 body 응답(게이트웨이/인프라 차단 등)은 HTTP 상태를 함께 노출 — 옛 AuthManagementTables 규약. */
const apiErrMessage = (e: unknown) => {
  const resp = (e as { response?: { status?: number; data?: { meta?: { message?: string } } } })?.response
  return resp?.data?.meta?.message ?? (resp?.status ? `처리에 실패했습니다. (HTTP ${resp.status})` : '처리에 실패했습니다.')
}

const hasCondition = (p: AuthManagementParams) => Object.values(p).some((v) => (Array.isArray(v) ? v.length > 0 : v != null))
const rowKeyOf = (r: Row) => String((r.key as string | number | undefined) ?? '')
const headKey = (r: { SPR_NM: string; AUTH_CD: string }) => `${r.SPR_NM}|${r.AUTH_CD}`
const itemKey = (r: { AUTH_CD: string; MENU_CD: string }) => `${r.AUTH_CD}|${r.MENU_CD}`
/** 기존 행의 화주·권한코드(복합키)가 조회 때 값에서 바뀌었나 — 키 변경 API 가 없어 저장을 막는다(PUT 은 권한명만 반영, 화면만 바뀌는 불일치 방지). */
const keyChanged = (r: HeadRow) => !r.__new && headKey(r) !== r.key
/** 하단 메뉴코드(매핑 키)가 조회 때 값에서 바뀌었나 — 바꾸는 API 가 없고, 편집값으로 보내면 다른 메뉴의 매핑을 덮는다(리뷰 gyoungjun). */
const itemKeyChanged = (r: ItemRow) => itemKey(r) !== r.key
/** 조회 조건이 같은가 — 같은 조건이면 캐시라 요청이 안 나가므로 그때만 무효화한다(다르면 새 키 쿼리가 스스로 받는다). */
const sameParams = (a: AuthManagementParams, b: AuthManagementParams) => JSON.stringify(a) === JSON.stringify(b)
/** 필수 셀 비어 있음 — 편집 셀 값은 문자열만 온다(select·input). 문자열이 아니면 빈 것으로 본다. */
const isBlankCell = (v: unknown) => typeof v !== 'string' || !v.trim()

function AuthManagement() {
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()

  // 계정은 마운트 시점 한 번 — 화주 전환은 reload 를 동반해 새 마운트를 만든다. 기준은 shipperCode(현재 화주, 전환 반영).
  //  잠금 판정은 2.0 공용 규칙 — FREE_SHIPPERS 4종 외는 자기 화주로 고정(LOGISPRD-14652).
  const accountRef = useRef<{ currentShipper: string; isFreeShipper: boolean }>()
  if (!accountRef.current) {
    const scope = shipperScopeOf(AuthStorage.user.get())
    accountRef.current = { currentShipper: scope.shipperCode, isFreeShipper: !scope.locked }
  }
  const { currentShipper, isFreeShipper } = accountRef.current
  const lockShipper = useCallback(
    (p: AuthManagementParams): AuthManagementParams => (isFreeShipper ? p : { ...p, shipperCode: currentShipper }),
    [isFreeShipper, currentShipper],
  )

  // 마운트 스냅샷은 useRef 로 — useMemo 는 캐시라 React 가 언제든 버릴 수 있다. 복원값도 잠금을 지난다.
  const restoredRef = useRef<AuthManagementParams | undefined>()
  const restoredReadRef = useRef(false)
  if (!restoredReadRef.current) {
    restoredReadRef.current = true
    const restored = fromSearchQuery(parsedQs)
    restoredRef.current = restored ? lockShipper(restored) : undefined
  }

  /** 화주 옵션 — 유저관리와 같은 API 재사용. 화주 고정 계정은 자기 화주만 남긴다. */
  const shipperQuery = useQuery<ShipperOption[], Error>({
    queryKey: authManagementQueryKeys.shipperOptions,
    queryFn: getMasterShipperOptions,
    meta: { noErrorMessage: true }, // 전역 QueryCache.onError 알럿을 끄고 아래 화면 문구만 낸다(두 번 뜨지 않게)
  })
  const shipperOptions = useMemo<ShipperOption[]>(() => {
    const all = shipperQuery.data ?? []
    return isFreeShipper ? all : all.filter((o) => o.value === currentShipper)
  }, [shipperQuery.data, isFreeShipper, currentShipper])
  // 화주 목록 실패 — 옵션이 비면 화주를 못 고른다. 화면은 그리되 이유를 알린다(조용히 빈 셀렉트로 두지 않는다).
  //  목록 조회와 같은 훅 — 재조회가 다시 실패해도(errorUpdatedAt 갱신) 다시 알린다.
  usePboQueryErrorToast(shipperQuery, MESSAGES.shipperLoadFailed, { preferFallback: true }) // 화면 문구 고정 — BE 문구로 덮이면 셀렉트가 왜 비었는지 모른다
  // 기본 화주 = 현재 화주. 통합 운영 계정은 목록에 없으면 첫 화주로 폴백(옛 화면 규약), 화주 고정 계정은 폴백 없이 자기 화주다.
  const defaultShipper = useMemo(() => {
    if (!isFreeShipper) return currentShipper
    return String((shipperOptions.find((o) => o.value === currentShipper) ?? shipperOptions[0])?.value ?? '')
  }, [shipperOptions, isFreeShipper, currentShipper])

  /** 마지막 조회 조건을 메뉴 세션·URL 에 남긴다. reload() 를 빼면 탭 스트립이 옛 조건을 되살린다. */
  const persist = useCallback(
    (next: AuthManagementParams) => {
      MenuStorage.menuOnSession.setSearch(next)
      menuSession.reload()
      replaceQuery(next)
    },
    [menuSession, replaceQuery],
  )

  /** 진입 자동조회 — 1.0 필수 필터 동작. URL 조건이 있으면 그것, 없으면 기본 화주다(옛 AuthManagementFilter 의 마운트 onSearch 자리). */
  const [params, setParams] = useState<AuthManagementParams>(() => restoredRef.current ?? {})
  const bootedRef = useRef(!!restoredRef.current)
  useEffect(() => {
    if (bootedRef.current || !defaultShipper) return
    bootedRef.current = true
    const next = { shipperCode: defaultShipper }
    persist(next)
    setParams(next)
  }, [defaultShipper, persist])

  /** 빈 조건(초기·초기화)에서는 조회하지 않는다 — 조건 없는 전체 조회 방지. */
  const listQuery = useGetAuthManagementList(params, hasCondition(params))
  const { data, isFetching } = listQuery
  usePboQueryErrorToast(listQuery)
  const saveHead = useSaveAuthorityHead()
  const deleteHead = useDeleteAuthorities()
  const saveMenus = useSaveAuthorityMenus()
  const queryClient = useQueryClient()

  // 상단 편집 상태 — key = 화주|권한코드(BE 복합키), 신규 행은 new-N. 선택 비우기는 뷰 몫([조회]·[초기화]·확인 성공 때).
  const [headRows, setHeadRows] = useState<HeadRow[]>([])
  const newSeqRef = useRef(0)
  useEffect(() => {
    setHeadRows((data?.contents ?? []).map((row) => ({ ...row, key: headKey(row) })))
  }, [data])

  /** 마스터-디테일 — 상단에서 클릭한 권한(화주+권한코드)으로 하단 메뉴를 조회한다(옛 useMasterDetail + useGetAuthorityMenus). */
  const [master, setMaster] = useState<Master | null>(null)
  const menusQuery = useGetAuthorityMenus(master?.shipperCode ?? '', master?.authCode ?? '')
  const { data: menuRows, isFetching: isFetchingMenus } = menusQuery
  usePboQueryErrorToast(menusQuery)
  const [itemRows, setItemRows] = useState<ItemRow[]>([])
  useEffect(() => {
    setItemRows((menuRows ?? []).map((row) => ({ ...row, key: itemKey(row) })))
  }, [menuRows])

  /** 체크 선택은 스코프별로 갈라 둔다 — ctx.selected 는 마지막으로 바뀐 표 하나라 위아래 표를 섞는다(5-A #3). 출처는 이것 하나다(onSelect). */
  const selectedRef = useRef<{ head: Set<string>; item: Set<string> }>({ head: new Set(), item: new Set() })
  const pickHead = (): HeadRow[] => headRows.filter((r) => selectedRef.current.head.has(r.key))
  const pickItem = (): ItemRow[] => itemRows.filter((r) => selectedRef.current.item.has(r.key))

  const isBusy = saveHead.isPending || deleteHead.isPending || saveMenus.isPending

  // ── 뷰 배선(Wiring) — 배선 객체는 매 렌더 새로 만든다(문서 51). ──
  const wiring: Wiring = {
    permission: usePermissionSlot('ma16'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { ma03HeadTable: coreTableLayoutSlot('MA16_HEAD'), ma03ItemTable: coreTableLayoutSlot('MA16_ITEM') },
    rows: {
      ROWS: () => ({ rows: headRows, total: headRows.length, loading: isFetching }),
      // 하단은 서버가 선택 권한의 메뉴만 준다 — 뷰의 AUTH_CD 필터는 그대로 통과한다.
      ROWS2: () => ({ rows: itemRows, total: itemRows.length, loading: isFetchingMenus }),
      // 권한코드 검색 모달 후보 = 이 화주의 권한 목록(1.0 AUTH_CD 헬퍼와 같은 규약) — 미저장 신규 행(빈 권한코드)은 뺀다.
      //  q.filters = 모달 [조회] 때의 적용 조건(뷰가 잡는다 · 열 때·[초기화] 때 비움 · [조회] 전엔 뷰가 표를 비워 그린다).
      //  목록은 이미 받아 둔 것이라 서버를 다시 부르지 않고 권한코드·권한명을 부분일치로 거른다 — 빈 조건 [조회] = 전체.
      ROWS3: (_demo, q) => filterHelperRows(headRows.filter((r) => !r.__new), q?.filters, AUTH_HELPER_KEYS),
    },
    actions: {
      /** [조회] — 화주는 필수다. 조건이 바뀌면 새 키 쿼리가 받고, 같은 조건이면 캐시라 그때만 무효화한다(둘 다 하면 옛 조건으로 한 번 더 나간다). 하단은 새 목록에서 다시 고른다.
       *  [추가]한 미저장 행은 걷는다 — 응답이 같으면 data 참조가 그대로라(structuralSharing) effect 가 안 돌아 서버에 없는 행이 남는다. */
      search: async (filters) => {
        const next = lockShipper(toParams(filters))
        if (!next.shipperCode) {
          notice.invalid(MESSAGES.noShipper)
          return false // 조회를 안 했으니 뷰가 페이저·선택·검색칸을 리셋하지 않게(생성본은 false 만 건너뛴다)
        }
        bootedRef.current = true
        persist(next)
        setHeadRows((prev) => prev.filter((r) => !r.__new))
        setMaster(null)
        const same = sameParams(next, params)
        setParams(next)
        if (same) await queryClient.invalidateQueries({ queryKey: authManagementQueryKeys.list(next) })
      },
      /** 편집 셀 — 표는 보이는 행만 돌려준다. 행 키로 찾아 바뀐 것만 갈아 끼운다. */
      ROWS_rowsChange: (changed) => {
        const byKey = new Map(changed.map((r) => [rowKeyOf(r), r]))
        setHeadRows((prev) => prev.map((r) => ({ ...r, ...(byKey.get(r.key) ?? {}) }) as HeadRow))
      },
      /** 하단 편집 셀 + 허용여부 전파(editCascade) — 바뀐 행만 전파 대상이다. */
      ROWS2_rowsChange: (changed) => {
        const byKey = new Map(changed.map((r) => [rowKeyOf(r), r]))
        setItemRows((prev) => {
          const changedKeys = new Set<string>()
          const merged = prev.map((r) => {
            const next = byKey.get(r.key)
            if (next && next[ITEM_EDIT_CASCADE.field] !== r[ITEM_EDIT_CASCADE.field]) changedKeys.add(r.key)
            return { ...r, ...(next ?? {}) } as ItemRow
          })
          return changedKeys.size ? cascadeMenuAct(merged, changedKeys) : merged
        })
      },
      /** 상단 [추가] — 빈 권한 행을 맨 위에(옛 appendHeadRow). 화주 = 조회 화주(화주 고정 계정은 현재 화주). 저장 때 __new 행은 POST. */
      hdAddButton: ({ defaults }) => {
        newSeqRef.current += 1
        const shipper = isFreeShipper ? String(params.shipperCode ?? '') : currentShipper
        setHeadRows((prev) => [{ SPR_NM: shipper, AUTH_CD: '', AUTH_NM: '', ...defaults, key: `new-${newSeqRef.current}`, __new: true }, ...prev])
      },
      /** 하단 [추가] — 매핑 추가 API 가 없다. 안내만. TODO(api) */
      itAddButton: () => {
        notice.invalid(MESSAGES.itemAddNoApi)
      },
      /** 상단 [저장] — 선택 행을 신규(POST)/기존(PUT 권한명)으로 가른다(옛 onConfirmOk head/save).
       *  검증 실패는 안내 뒤 throw — 확인창·체크 선택을 남긴다(false 는 "성공인데 토스트 생략"이라 생성본이 선택을 비운다, 가이드 52 규칙 10). API 실패는 알럿 뒤 throw. 상하단 [저장]·[삭제] 모두 같다. */
      ma03HeadSaveConfirm_ok: async () => {
        const targets = pickHead()
        if (targets.length === 0) {
          throw invalidError(MESSAGES.noSelection)
        }
        if (targets.some((r) => isBlankCell(r.SPR_NM) || isBlankCell(r.AUTH_CD) || isBlankCell(r.AUTH_NM))) {
          throw invalidError(MESSAGES.requiredHeadCells)
        }
        // 화주코드 셀이 편집 가능하다 — 화주 고정 계정이 다른 화주로 고쳐 저장하면 권한 밖 쓰기가 된다.
        if (!isFreeShipper && targets.some((r) => r.SPR_NM !== currentShipper)) {
          throw invalidError(MESSAGES.shipperLocked)
        }
        if (targets.some(keyChanged)) {
          throw invalidError(MESSAGES.keyLocked)
        }
        try {
          await saveHead.mutateAsync(targets.map((r) => ({ shipperCode: r.SPR_NM, authCode: r.AUTH_CD, authName: r.AUTH_NM, isNew: !!r.__new })))
          // [MOMS 공통] 저장 후 자동 재조회 금지(옛 AuthManagementTables 규약) — 재조회는 체크 안 한 행의 편집분·미저장 신규 행을 지운다.
          //  저장한 신규 행은 기존 행으로 바꾼다(__new 해제 · key 재계산). 다음 [조회]·재진입이 서버 값을 읽는다.
          const savedKeys = new Set(targets.map((r) => r.key))
          setHeadRows((prev) => prev.map((r) => (savedKeys.has(r.key) && r.__new ? { ...r, __new: undefined, key: headKey(r) } : r)))
        } catch (e) {
          const msg = apiErrMessage(e)
          throw failureError(msg)
        }
      },
      /** 상단 [삭제] — DELETE /authorities/{authCode}. 사용 중 권한은 BE 가 400. 삭제한 권한이 하단에 걸려 있으면 하단을 비운다. */
      ma03HeadDeleteConfirm_ok: async () => {
        const targets = pickHead()
        if (targets.length === 0) {
          throw invalidError(MESSAGES.noDeleteSelection)
        }
        if (!isFreeShipper && targets.some((r) => r.SPR_NM !== currentShipper)) {
          throw invalidError(MESSAGES.shipperLocked)
        }
        // 편집된 화주·권한코드로 DELETE 가 나가면 고르지 않은 다른 권한이 지워진다(리뷰 gyoungjun) — 저장과 같은 가드.
        if (targets.some(keyChanged)) {
          throw invalidError(MESSAGES.keyLocked)
        }
        // 미저장 신규 행은 서버에 없다 — 로컬에서만 지운다(옛 규약).
        const saved = targets.filter((r) => !r.__new)
        const newKeys = new Set(targets.filter((r) => r.__new).map((r) => r.key))
        if (newKeys.size) setHeadRows((prev) => prev.filter((r) => !newKeys.has(r.key)))
        if (saved.length === 0) return
        try {
          await deleteHead.mutateAsync(saved.map((r) => ({ shipperCode: r.SPR_NM, authCode: r.AUTH_CD })))
          // [MOMS 공통] 삭제 후 자동 재조회 금지 — 지운 행만 로컬에서 뺀다(옛 규약).
          const gone = new Set(saved.map((r) => r.key))
          setHeadRows((prev) => prev.filter((r) => !gone.has(r.key)))
          if (master && saved.some((r) => r.SPR_NM === master.shipperCode && r.AUTH_CD === master.authCode)) setMaster(null)
        } catch (e) {
          const msg = apiErrMessage(e)
          throw failureError(msg)
        }
      },
      /** 하단 [저장] — 선택 행의 허용여부·버튼권한·필터여부를 PUT /authorities/{authCode}/menus 로(옛 onConfirmOk item/save). */
      ma03ItemSaveConfirm_ok: async () => {
        if (!master) {
          throw invalidError(MESSAGES.noMaster)
        }
        const targets = pickItem()
        if (targets.length === 0) {
          throw invalidError(MESSAGES.noSelection)
        }
        if (targets.some((r) => isBlankCell(r.MENU_ACT))) {
          throw invalidError(MESSAGES.requiredItemCells)
        }
        if (targets.some(itemKeyChanged)) {
          throw invalidError(MESSAGES.itemKeyLocked)
        }
        try {
          await saveMenus.mutateAsync({
            shipperCode: master.shipperCode,
            authCode: master.authCode,
            items: targets.map((r) => ({ menuCode: r.MENU_CD, menuActive: r.MENU_ACT === 'Y', buttonLevel: String(r.BUTTON_LV ?? ''), filterActive: r.FILTER_YN === 'Y' })),
          })
          // [MOMS 공통] 저장 후 자동 재조회 금지 — 재조회는 전파(cascade)만 되고 체크 안 한 하위 행을 되돌린다. 수정자·수정일시는 다음 조회 때 읽는다.
        } catch (e) {
          const msg = apiErrMessage(e)
          throw failureError(msg)
        }
      },
      /** 하단 [삭제] — API 가 없다. 여는 단계(opens)에서 막으므로 여기는 닿지 않는다. TODO(api) */
      // 닿더라도 다른 확인 슬롯처럼 안내 뒤 throw 한다(확인창·선택 유지).
      ma03ItemDeleteConfirm_ok: () => {
        throw invalidError(MESSAGES.itemDeleteNoApi)
      },
      /** 권한코드 검색 모달 [선택] — 생성본이 고른 행의 AUTH_CD 를 조회영역에 넣고 닫는다. 여기서 할 일은 없다. */
      ma03AuthSearchModal_ok: () => undefined,
    },
    /** 상단 행 클릭(onPick) — 그 권한의 메뉴를 하단에 조회한다. 해제(재클릭)면 하단을 비운다.
     *  미저장 신규 행은 서버에 없어 조회가 404("Authority not found")다 — 조회하지 않고 하단을 비운다(1.0 MA03 EDITTED 'I' 건너뜀).
     *  복합키(화주·권한코드)를 고친 기존 행도 같다 — 편집값으로 조회하면 404 이거나 다른 권한이 하단에 떠 하단 [저장]이 그 권한으로 나간다. */
    onPick: (rows) => {
      const r = rows[0] as HeadRow | undefined
      setMaster(r && !r.__new && !keyChanged(r) && r.SPR_NM && r.AUTH_CD ? { shipperCode: String(r.SPR_NM), authCode: String(r.AUTH_CD) } : null)
    },
    /** 체크 선택 — 스코프별로 갈라 둔다(위아래 표의 빈 신호가 서로 덮지 않게). */
    onSelect: (rows, scope) => {
      const keys = new Set(rows.map(rowKeyOf))
      if (scope === HEAD_SCOPE) selectedRef.current.head = keys
      else if (scope === ITEM_SCOPE) selectedRef.current.item = keys
    },
    options: {
      MA03SHIPPER_OPTIONS: () => shipperOptions as Option[],
      // 화주코드 편집 셀도 같은 목록(실 화주). 데모 옵션(value=label)이 나가면 저장값이 표시명이 된다.
      ROWS_SPR_NM: () => shipperOptions as CellOption[],
      ROWS2_MENU_ACT: () => YN_OPTIONS,
      ROWS2_BUTTON_LV: () => BUTTON_LEVEL_OPTIONS,
      ROWS2_FILTER_YN: () => YN_OPTIONS,
    },
    /** 화주 고정 계정은 화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 기본값·옵션 축소가 따로 고정한다 */
    filterDisabled: isFreeShipper ? undefined : { SPR_NM: true },
    filterDefaults: { ma03FilterCard: (demo) => ({ ...demo, SPR_NM: defaultShipper || undefined }) },
    filterInitial: { ma03FilterCard: (defaults) => ({ ...defaults, ...toFilterValues(restoredRef.current) }) },
    useBusy: () => isBusy,
    opens: {
      // TODO(api): 메뉴 매핑 삭제 API 가 생기면 이 가드를 풀고 ma03ItemDeleteConfirm_ok 에 배선한다.
      itDeleteButton: () => {
        notice.invalid(MESSAGES.itemDeleteNoApi)
        return false
      },
    },
    /** [초기화] — 저장된 조회 조건과 상하 표까지 비운다(옛 onReset). 다음 진입이 기본값으로 열리게 한다. */
    onReset: () => {
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
      setParams({})
      setMaster(null)
      bootedRef.current = true
    },
  }

  // 뷰는 조회영역 기본값을 마운트 때 한 번만 읽는다 — 화주 옵션이 정해진 뒤 한 번만 그린다(리마운트 없음, #777 리뷰).
  if (shipperQuery.isPending) return null
  return <AuthManagementPageView wiring={wiring} />
}

export default AuthManagement
